define("shared/utils/formatters/format-hashtag-trends", ["exports", "@ember/utils", "lodash", "later/utils/object-methods", "later/utils/time-format"], function (_exports, _utils, _lodash, _objectMethods, _timeFormat) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.fillMissingHashtagData = fillMissingHashtagData;
  _exports.groupWeeklyDataByHashtag = groupWeeklyDataByHashtag;
  _exports.sanitizeHashtagsOutsideCutoffRank = sanitizeHashtagsOutsideCutoffRank;
  0; //eaimeta@70e063a35619d71f0,"@ember/utils",0,"lodash",0,"later/utils/object-methods",0,"later/utils/time-format"eaimeta@70e063a35619d71f
  const numberOfWeeks = 8;
  const weekDurationInS = _timeFormat.convert.day(7).toSeconds();
  const defaultWeek = {
    startTime: null,
    endTime: null,
    numberOfPosts: null,
    percentagePosts: null,
    rank: null,
    totalPosts: null
  };

  /**
   * Strip hashtags that are always outside the cutoff rank for the entire graph
   *
   * @method _removeHashtagsAlwaysOutsideCutoffRank
   * @param {(AnalyticsError|FormattedHashtagTrends)} formattedHashtagTrends
   * @param {Number} maxRank
   *
   * @returns {(AnalyticsError|FormattedHashtagTrends)} sanitized formattedHashtagTrends object
   * @protected
   */
  const _removeHashtagsAlwaysOutsideCutoffRank = (formattedHashtagTrends, maxRank) => (0, _objectMethods.objectFilter)(formattedHashtagTrends, _ref => {
    let [, value] = _ref;
    return !value.every(_ref2 => {
      let {
        rank
      } = _ref2;
      return rank > maxRank;
    });
  });

  /**
   * Strip hashtags over all time that are outside the cutoff rank in the most recent week
   *
   * @method _sanitizePointsNotInRecentWeek
   * @param {(AnalyticsError|FormattedHashtagTrends)} formattedHashtagTrends
   * @param {Number} minRank
   * @param {Number} maxRank
   * @param {Number} latestStartTime
   *
   * @returns {(AnalyticsError|FormattedHashtagTrends)} sanitized formattedHashtagTrends object
   * @protected
   */
  const _sanitizePointsNotInRecentWeek = (formattedHashtagTrends, minRank, maxRank, latestStartTime) => {
    const latestHashtags = Object.keys((0, _objectMethods.objectFilter)(formattedHashtagTrends, _ref3 => {
      let [, value] = _ref3;
      const latestValue = value.find(_ref4 => {
        let {
          startTime
        } = _ref4;
        return Math.abs(startTime - latestStartTime) < _timeFormat.convert.day(7).toSeconds();
      });
      return latestValue ? latestValue.rank <= maxRank && latestValue.rank >= minRank : false;
    }));
    return (0, _objectMethods.objectFilter)(formattedHashtagTrends, _ref5 => {
      let [key] = _ref5;
      return latestHashtags.includes(key);
    });
  };

  /**
   * Converts rawHashtagTrends to be grouped by hashtag into a FormattedHashtagTrends
   *
   * @method groupWeeklyDataByHashtag
   * @param {(AnalyticsError|FormattedIndustryHashtags)} rawHashtagTrends
   *
   * @returns {(AnalyticsError|FormattedHashtagTrends)}
   */
  function groupWeeklyDataByHashtag(rawHashtagTrends) {
    if ((0, _utils.isEmpty)(rawHashtagTrends) || (0, _utils.isPresent)(rawHashtagTrends.error)) {
      return Array.isArray(rawHashtagTrends) ? {} : rawHashtagTrends;
    }
    const transformedHashtags = {};
    rawHashtagTrends.forEach(_ref6 => {
      let {
        startTime,
        endTime,
        hashtags,
        totalPosts
      } = _ref6;
      hashtags.forEach(_ref7 => {
        let {
          tag,
          numberOfPosts,
          percentagePosts,
          rank
        } = _ref7;
        const dataPoint = {
          startTime,
          endTime,
          numberOfPosts,
          totalPosts,
          percentagePosts,
          rank
        };
        if (tag in transformedHashtags) {
          transformedHashtags[tag].push(dataPoint);
        } else {
          transformedHashtags[tag] = [dataPoint];
        }
      });
    });
    return transformedHashtags;
  }

  /**
   * Takes a formattedHashtagTrends object, and fills missing
   * weekly hashtag values with default blank data points
   *
   * @method fillMissingHashtagData
   * @param {(AnalyticsError|FormattedHashtagTrends)} formattedHashtagTrends
   * @param {Array} unixStartTimes
   *
   * @returns {(AnalyticsError|FormattedHashtagTrends)}
   */
  function fillMissingHashtagData(formattedHashtagTrends, unixStartTimes) {
    if ((0, _utils.isEmpty)(formattedHashtagTrends) || (0, _utils.isPresent)(formattedHashtagTrends.error)) {
      return formattedHashtagTrends;
    }
    const defaultHashtagData = Array(numberOfWeeks).fill(defaultWeek).map((defaultWeek, index) => ({
      ...defaultWeek,
      ...{
        startTime: unixStartTimes[index],
        endTime: unixStartTimes[index] + weekDurationInS
      }
    }));
    return (0, _lodash.fromPairs)(Object.entries(formattedHashtagTrends).map(_ref8 => {
      let [hashtag, hashtagData] = _ref8;
      if (hashtagData.length < numberOfWeeks) {
        const filledHashtagData = defaultHashtagData.map(defaultPoint => {
          const currWeekPoint = hashtagData.find(_ref9 => {
            let {
              startTime
            } = _ref9;
            return startTime === defaultPoint.startTime;
          });
          return currWeekPoint ? currWeekPoint : {
            ...defaultPoint
          };
        });
        return [hashtag, filledHashtagData];
      }
      return [hashtag, hashtagData];
    }));
  }

  /**
   * Strip hashtags that are outside the cutoff rank from the formattedHashtagTrends object
   *
   * @method sanitizeHashtagsOutsideCutoffRank
   * @param {(AnalyticsError|FormattedHashtagTrends)} formattedHashtagTrends
   * @param {Number} minRank
   * @param {Number} maxRank
   * @param {Array} unixStartTimes
   *
   * @returns {(AnalyticsError|FormattedHashtagTrends)} sanitized formattedHashtagTrends object
   */
  function sanitizeHashtagsOutsideCutoffRank(formattedHashtagTrends, minRank, maxRank, unixStartTimes) {
    if ((0, _utils.isEmpty)(formattedHashtagTrends) || (0, _utils.isPresent)(formattedHashtagTrends.error)) {
      return formattedHashtagTrends;
    }
    return _sanitizePointsNotInRecentWeek(_removeHashtagsAlwaysOutsideCutoffRank(formattedHashtagTrends, maxRank), minRank, maxRank, (0, _lodash.last)(unixStartTimes));
  }
});